import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Row, Col } from 'antd';
import {
  // SwitchCommon,
  ButtonCommon,
  ErrorMessage,
  PrefixedInput,
  showAlertNotification,
  InputCommon,
  LabelWithRequired,
  SelectCommon,
} from 'components/common';
import { currencyRules, phoneNumberRule } from 'utils/formRules';
import { AddStaffModalProps } from 'types/common';
import { useGetAllRolesQuery } from 'services';
const { TextArea } = Input;

const AddStaffModal: React.FC<AddStaffModalProps> = ({
  isOpen,
  onCancel,
  onSubmit,
  onGenerate,
  form,
  editMode = false,
  staffData = null,
  isSubmitting = false,
}) => {
  const [modalOpened, setModalOpened] = useState(false);
  const [codePrefix, setCodePrefix] = useState<string>('');
  const [genCode, setGenCode] = useState<string>('');
  const { data } = useGetAllRolesQuery({
    query: {
      size: 'unlimited',
    },
  });
  useEffect(() => {
    if (genCode) {
      const prefix = genCode.substring(0, 6);
      setCodePrefix(prefix);
      form.setFieldsValue({ baseUser: { code: genCode } });
    }
  }, [genCode, form]);

  useEffect(() => {
    const fetchCode = async () => {
      if (isOpen && !modalOpened && onGenerate) {
        setModalOpened(true);

        if (!editMode) {
          const code = await onGenerate();
          setGenCode(code);
        }
      }

      if (!isOpen) {
        setModalOpened(false);
      }
    };
    fetchCode();
  }, [isOpen, editMode, onGenerate, modalOpened]);

  useEffect(() => {
    if (isOpen) {
      if (editMode && staffData) {
        form.setFieldsValue({
          baseUser: {
            name: staffData['baseUser.name'],
            code: staffData['baseUser.code'],
            email: staffData['baseUser.email'],
            phoneNumber: staffData['baseUser.phoneNumber'],
            address: staffData.address,
            isActive: staffData['baseUser.isActive'],
          },
          password: staffData.password || '',
          note: staffData.note,
        });
        if (staffData.roleId) {
          form.setFieldsValue({
            roleId: staffData.roleId,
          });
        }
        if (staffData['baseUser.code']) {
          const prefix = staffData['baseUser.code'].substring(0, 6);
          setCodePrefix(prefix);
        }
      } else if (!editMode) {
        form.resetFields();
        form.setFieldsValue({
          baseUser: {
            isActive: true,
          },
        });
      }
    }
  }, [isOpen, form, editMode, staffData]);

  const getModalTitle = () => {
    if (editMode) {
      return 'CHỈNH SỬA NHÂN VIÊN';
    }
    return 'THÊM NHÂN VIÊN MỚI';
  };

  const getModalDescription = () => {
    if (editMode) {
      return 'Vui lòng chỉnh sửa thông tin của nhân viên theo nhu cầu của bạn';
    }
    return 'Vui lòng điền đầy đủ thông tin của nhân viên mới';
  };

  const getSubmitButtonText = () => {
    if (editMode) {
      return 'Cập nhật';
    }
    return 'Thêm mới';
  };

  return (
    <Modal
      title={
        <div className='flex flex-col bg-white py-[12px] px-[24px] rounded-lg gap-2'>
          <h2 className='text-[18px] font-bold leading-[24px]'>{getModalTitle()}</h2>
          <p className='text-[14px] leading-[20px] text-[var(--textBlack56)] font-normal'>{getModalDescription()}</p>
        </div>
      }
      open={isOpen}
      // onCancel={onCancel}
      footer={null}
      width={1255}
      centered
      className='staff-modal'
      destroyOnClose
      closeIcon={null}
      styles={{
        wrapper: {
          alignItems: 'center',
          paddingTop: '10vh',
        },
      }}
    >
      <div className='px-[24px] py-[12] mb-[12px]'>
        <Form
          form={form}
          requiredMark={false}
          layout='vertical'
          className='bg-white rounded-lg py-[24px] px-[48px] font-medium text-[14px] leading-[20px]'
        >
          <Row gutter={[24, 0]}>
            <Col span={24}>
              <Form.Item
                label={<LabelWithRequired label='Họ và tên' />}
                name={['baseUser', 'name']}
                rules={[
                  { required: true, message: <ErrorMessage message='Họ và tên là bắt buộc!' /> },
                  { max: 100, message: <ErrorMessage message='Họ và tên không được vượt quá 100 ký tự!' /> },
                ]}
              >
                <InputCommon
                  className='!border-0 !pl-0 rounded-none font-bold !text-[20px] !leading-[24px] !border-b-[1px] border-[var(--textBlack12)] !transition-none !shadow-none'
                  placeholder='Nhập vào tên nhân viên'
                  radius='none'
                  maxLength={100}
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label={<LabelWithRequired label='Mã nhân viên' />}
                name={['baseUser', 'code']}
                rules={[
                  { required: true, message: <ErrorMessage message='Vui lòng nhập mã nhân viên!' /> },
                  { max: 20, message: <ErrorMessage message='Mã nhân viên không được vượt quá 20 ký tự!' /> },
                  {
                    pattern: /^[a-zA-Z0-9_]+$/,
                    message: <ErrorMessage message='Mã nhân viên chỉ được chứa chữ cái, số và dấu gạch dưới!' />,
                  },
                ]}
              >
                <PrefixedInput
                  prefix={codePrefix}
                  maxEditableLength={6}
                  placeholder='Nhập mã'
                  disabled={editMode && !!staffData?.code}
                  className='!text-[14px] !leading-[20px] !h-[38px]'
                  inputProps={{
                    suffix: (!editMode || !staffData?.code) && (
                      <span
                        className='text-[var(--primaryColor500)] cursor-pointer'
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (onGenerate) {
                            try {
                              const code = await onGenerate();
                              if (code && code.length >= 6) {
                                setCodePrefix(code.substring(0, 6));
                                form.setFieldsValue({ baseUser: { ...form.getFieldsValue().baseUser, code } });
                              }
                            } catch (error) {
                              console.error('Error generating code:', error);
                            }
                          }
                        }}
                      >
                        Generate
                      </span>
                    ),
                    hardSize: 'md2',
                  }}
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label={<LabelWithRequired label='Số điện thoại' />}
                name={['baseUser', 'phoneNumber']}
                rules={[phoneNumberRule()]}
              >
                <InputCommon placeholder='Nhập số điện thoại' hardSize='md2' />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label={<LabelWithRequired label='Gmail' />}
                name={['baseUser', 'email']}
                rules={[
                  { required: true, message: <ErrorMessage message='Vui lòng nhập email!' /> },
                  {
                    type: 'email',
                    message: <ErrorMessage message='Email không hợp lệ!' />,
                  },
                ]}
              >
                <InputCommon placeholder='Nhập email' disabled={editMode} hardSize='md2' />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label={<LabelWithRequired label='Vai trò' />} name='roleId' rules={currencyRules}>
                <SelectCommon
                  placeholder='Chọn vai trò'
                  options={(data?.data.rows || [])?.map((item) => ({
                    label: item.name as string,
                    value: item.id as string,
                  }))}
                  value={staffData?.roleId}
                />
              </Form.Item>
            </Col>
            {
              <Col span={8}>
                <Form.Item
                  label='Mật khẩu'
                  name='password'
                  // rules={passNewRules(form.getFieldValue('baseUser.email'))}
                >
                  <InputCommon hardSize='md2' type='password' placeholder='Nhập vào mật khẩu' />
                </Form.Item>
              </Col>
            }
            <Col span={24}>
              <Form.Item
                label='Thông tin địa chỉ'
                name={['baseUser', 'address']}
                rules={[{ max: 200, message: <ErrorMessage message='Địa chỉ không được vượt quá 200 ký tự!' /> }]}
              >
                <InputCommon placeholder='Nhập địa chỉ' hardSize='md2' />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                className='font-normal'
                label='Mô tả'
                name='note'
                rules={[{ max: 1000, message: <ErrorMessage message='Mô tả không được vượt quá 1000 ký tự!' /> }]}
              >
                <TextArea rows={4} placeholder='Nhập vào mô tả...' />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
      <div className='flex justify-center gap-3 mt-6'>
        <ButtonCommon color='none' size='md' radius='rounded' onClick={onCancel} disabled={isSubmitting}>
          Hủy
        </ButtonCommon>
        <ButtonCommon
          color='primary'
          size='md'
          radius='rounded'
          loading={isSubmitting}
          onClick={() => {
            form
              .validateFields()
              .then(onSubmit)
              .catch((errorInfo) => {
                showAlertNotification({
                  type: 'warning',
                  title: 'Thông báo',
                  message: 'Vui lòng nhập đúng thông tin nhân viên',
                });
                console.log('Validation failed:', errorInfo);
              });
          }}
        >
          {getSubmitButtonText()}
        </ButtonCommon>
      </div>
    </Modal>
  );
};

export default AddStaffModal;
